import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  LayoutDashboard,
  Eye,
  Package,
  ShoppingCart,
  Search,
  FileSpreadsheet,
  Settings,
  LogOut,
  ExternalLink,
  CheckCircle2,
  Banknote,
  Sparkles,
  Tag,
  Grid,
  Layers,
  ImageIcon,
  ArrowLeft,
  Upload,
  Download,
  AlertCircle,
  FileText,
  RefreshCw,
  ShoppingBag,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  MapPin,
  X,
  Copy,
  Check,
} from "lucide-react";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from "@mui/material/Backdrop";
import config from "../../config/config";
import { brandLogout, setBrandSession } from "../../redux/slices/brandAuthSlice";
import { isTokenExpired } from "../../redux/slices/adminSlice";

const Pagination = ({ pagination, onPageChange }) => {
  const [inputPage, setInputPage] = useState(pagination?.page || pagination?.currentPage || 1);

  useEffect(() => {
    setInputPage(pagination?.page || pagination?.currentPage || 1);
  }, [pagination?.page, pagination?.currentPage]);

  if (!pagination || pagination.totalPages <= 1) return null;
  const page = parseInt(pagination.page || pagination.currentPage, 10);
  const totalPages = parseInt(pagination.totalPages, 10);

  const handleInputSubmit = (e) => {
    if (e.key === "Enter" || e.type === "blur") {
      let newPage = parseInt(inputPage, 10);
      if (isNaN(newPage)) newPage = page;
      if (newPage < 1) newPage = 1;
      if (newPage > totalPages) newPage = totalPages;
      setInputPage(newPage);
      if (newPage !== page) {
        onPageChange(newPage);
      }
    }
  };

  return (
    <div className="flex justify-center items-center gap-4 py-4 border-t">
      <button
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        className="px-4 py-2 border rounded font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 transition-colors text-xs"
      >
        Previous
      </button>
      <span className="text-gray-600 font-medium flex items-center gap-2 text-xs">
        Page
        <input
          type="number"
          value={inputPage}
          onChange={(e) => setInputPage(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleInputSubmit(e)}
          onBlur={handleInputSubmit}
          className="border rounded px-2 py-1 w-14 text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 text-xs"
          min={1}
          max={totalPages}
        />
        of {totalPages}
      </span>
      <button
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        className="px-4 py-2 border rounded font-medium text-gray-700 disabled:opacity-50 hover:bg-gray-50 transition-colors text-xs"
      >
        Next
      </button>
    </div>
  );
};

// Real WhatsApp Official SVG Icon
const WhatsAppIcon = ({ className = "w-4 h-4", fill = "#25D366" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

// Real Instagram Official SVG Icon
const InstagramIcon = ({ className = "w-4 h-4", fill = "#E1306C" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill={fill}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

export default function BrandDashboard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { token, brandInfo } = useSelector((state) => state.brandAuth);

  const activeToken = token || localStorage.getItem("brandToken");
  const storedBrand = brandInfo || (localStorage.getItem("brandInfo") ? JSON.parse(localStorage.getItem("brandInfo")) : null);

  useEffect(() => {
    if (!activeToken || isTokenExpired(activeToken)) {
      dispatch(brandLogout());
      navigate("/brand-login");
    }
  }, [activeToken, dispatch, navigate]);

  const isOnboarded = storedBrand?.brand_type === "onboarded";
  const hasAdminToken = Boolean(localStorage.getItem("adminToken"));

  const [activeTab, setActiveTab] = useState("overview");

  // Overview states
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewData, setOverviewData] = useState(null);
  const [overviewDraftStartDate, setOverviewDraftStartDate] = useState("");
  const [overviewDraftEndDate, setOverviewDraftEndDate] = useState("");
  const [overviewAppliedStartDate, setOverviewAppliedStartDate] = useState("");
  const [overviewAppliedEndDate, setOverviewAppliedEndDate] = useState("");

  // Impressions Tab states
  const [impressionsLoading, setImpressionsLoading] = useState(false);
  const [impressionsData, setImpressionsData] = useState([]);
  const [impressionsTotal, setImpressionsTotal] = useState(0);
  const [impressionsPage, setImpressionsPage] = useState(1);
  const [impressionsPagination, setImpressionsPagination] = useState(null);
  const [impressionsSort, setImpressionsSort] = useState("latest");
  const [impressionsDraftStartDate, setImpressionsDraftStartDate] = useState("");
  const [impressionsDraftEndDate, setImpressionsDraftEndDate] = useState("");
  const [impressionsAppliedStartDate, setImpressionsAppliedStartDate] = useState("");
  const [impressionsAppliedEndDate, setImpressionsAppliedEndDate] = useState("");

  // Product Analytics Tab states
  const [productAnalyticsLoading, setProductAnalyticsLoading] = useState(false);
  const [productAnalyticsData, setProductAnalyticsData] = useState([]);
  const [productAnalyticsTotal, setProductAnalyticsTotal] = useState(0);
  const [productAnalyticsClicksTotal, setProductAnalyticsClicksTotal] = useState(0);
  const [productAnalyticsBuysTotal, setProductAnalyticsBuysTotal] = useState(0);
  const [productAnalyticsPage, setProductAnalyticsPage] = useState(1);
  const [productAnalyticsPagination, setProductAnalyticsPagination] = useState(null);
  const [productAnalyticsSort, setProductAnalyticsSort] = useState("latest_product_click");
  const [productAnalyticsSearch, setProductAnalyticsSearch] = useState("");
  const [productAnalyticsDraftStartDate, setProductAnalyticsDraftStartDate] = useState("");
  const [productAnalyticsDraftEndDate, setProductAnalyticsDraftEndDate] = useState("");
  const [productAnalyticsAppliedStartDate, setProductAnalyticsAppliedStartDate] = useState("");
  const [productAnalyticsAppliedEndDate, setProductAnalyticsAppliedEndDate] = useState("");
  const [expandedProductIds, setExpandedProductIds] = useState({});

  // Buy Clicks Tab states (Ungrouped individual records with channel filter)
  const [buyClicksLoading, setBuyClicksLoading] = useState(false);
  const [buyClicksData, setBuyClicksData] = useState([]);
  const [buyClicksChannelCounts, setBuyClicksChannelCounts] = useState({
    all: 0,
    direct_order: 0,
    whatsapp: 0,
    instagram: 0,
    website: 0,
  });
  const [buyClicksFilter, setBuyClicksFilter] = useState("all");
  const [buyClicksSearch, setBuyClicksSearch] = useState("");
  const [buyClicksTotal, setBuyClicksTotal] = useState(0);
  const [buyClicksTotalPrice, setBuyClicksTotalPrice] = useState(0);
  const [buyClicksPage, setBuyClicksPage] = useState(1);
  const [buyClicksPagination, setBuyClicksPagination] = useState(null);
  const [buyClicksDraftStartDate, setBuyClicksDraftStartDate] = useState("");
  const [buyClicksDraftEndDate, setBuyClicksDraftEndDate] = useState("");
  const [buyClicksAppliedStartDate, setBuyClicksAppliedStartDate] = useState("");
  const [buyClicksAppliedEndDate, setBuyClicksAppliedEndDate] = useState("");

  // Direct Order Details Popup Modal state
  const [selectedDirectOrder, setSelectedDirectOrder] = useState(null);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const toggleExpandProduct = (id) => {
    setExpandedProductIds((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleCopyPhone = (phone) => {
    if (!phone) return;
    navigator.clipboard.writeText(phone);
    setCopiedPhone(true);
    setTimeout(() => setCopiedPhone(false), 2000);
  };

  // Search Insights Tab states
  const [queriesLoading, setQueriesLoading] = useState(false);
  const [searchQueriesData, setSearchQueriesData] = useState([]);
  const [queriesTotal, setQueriesTotal] = useState(0);
  const [queryPage, setQueryPage] = useState(1);
  const [queryPagination, setQueryPagination] = useState(null);
  const [queriesDraftStartDate, setQueriesDraftStartDate] = useState("");
  const [queriesDraftEndDate, setQueriesDraftEndDate] = useState("");
  const [queriesAppliedStartDate, setQueriesAppliedStartDate] = useState("");
  const [queriesAppliedEndDate, setQueriesAppliedEndDate] = useState("");

  // Brand Settings states
  const parseStoredPhone = (raw) => {
    if (!raw) return { code: "", number: "" };
    const str = String(raw).trim();
    if (str.includes(" ")) {
      const parts = str.split(" ");
      return {
        code: parts[0].replace(/[^0-9]/g, ""),
        number: parts.slice(1).join("").replace(/[^0-9]/g, ""),
      };
    }
    const clean = str.replace(/^\+/, "").replace(/[^0-9]/g, "");
    if (clean.length > 10) {
      return { code: clean.slice(0, 2), number: clean.slice(2) };
    }
    return { code: "", number: clean };
  };

  const [settingsCountryCode, setSettingsCountryCode] = useState(() => parseStoredPhone(storedBrand?.whatsapp_number).code);
  const [settingsPhoneNumber, setSettingsPhoneNumber] = useState(() => parseStoredPhone(storedBrand?.whatsapp_number).number);
  const [settingsInstagram, setSettingsInstagram] = useState(storedBrand?.instagram_handle || "");
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState(false);

  // Catalog Management states (Onboarded Brands)
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogData, setCatalogData] = useState(null);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogSearch, setCatalogSearch] = useState("");
  const [catalogSubView, setCatalogSubView] = useState("catalog"); // "catalog" | "guide"

  // Staged Preview states
  const [isStagedMode, setIsStagedMode] = useState(false);
  const [stagedProducts, setStagedProducts] = useState([]);

  // CSV Upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(null);
  const [clientErrors, setClientErrors] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [showUploadDropzone, setShowUploadDropzone] = useState(false);

  // Modal Dialog & Notification states
  const [modalConfig, setModalConfig] = useState(null);
  const [catalogSyncSuccess, setCatalogSyncSuccess] = useState(null);
  const prevCatalogStatusRef = useRef(null);

  // Phase 3 Catalog Sync & Status states
  const [syncing, setSyncing] = useState(false);
  const [catalogStatusInfo, setCatalogStatusInfo] = useState({
    status: "idle",
    updatedAt: null,
    error: null,
    hasStaged: false,
    stagedCount: 0,
  });

  // Auto-dismiss parsed products success message after 30 seconds
  useEffect(() => {
    if (uploadSuccess) {
      const timer = setTimeout(() => {
        setUploadSuccess(null);
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [uploadSuccess]);

  // Clears frontend session cache strictly for this brand, leaving other brands untouched
  const clearBrandSessionCache = useCallback((brandName) => {
    if (!brandName) return;
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.startsWith(`brandProducts_${brandName}`) ||
            key.startsWith(`activeFilters_${brandName}`) ||
            key.startsWith(`activeSort_${brandName}`) ||
            key.startsWith(`aiSearchQuery_${brandName}`))
        ) {
          sessionStorage.removeItem(key);
        }
      }
    } catch (e) {
      console.error("Error clearing brand session cache:", e);
    }
  }, []);

  useEffect(() => {
    if (storedBrand) {
      const parsed = parseStoredPhone(storedBrand.whatsapp_number);
      setSettingsCountryCode(parsed.code);
      setSettingsPhoneNumber(parsed.number);
      setSettingsInstagram(storedBrand.instagram_handle || "");
    }
  }, [storedBrand]);

  const formatTimestamp = (t) => (t ? new Date(t).toLocaleString("en-US", { timeZone: "UTC" }) : "-");

  // Horizontal Navigation Tabs
  const tabsList = [
    { key: "overview", label: "Overview", icon: LayoutDashboard },
    { key: "product-analytics", label: "Product Analytics", icon: Package },
    { key: "buy-clicks", label: "Buy Clicks", icon: ShoppingCart },
    { key: "impressions", label: "Impressions", icon: Eye },
    { key: "search-insights", label: "Search Insights", icon: Search },
    ...(isOnboarded ? [{ key: "manage-products", label: "Manage Products", icon: FileSpreadsheet }] : []),
    { key: "settings", label: "Brand Settings", icon: Settings },
  ];

  /* -------------------- API FETCHERS -------------------- */

  const fetchOverview = useCallback(async () => {
    if (!activeToken) return;
    setOverviewLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/overview`, {
        params: {
          startDate: overviewAppliedStartDate || undefined,
          endDate: overviewAppliedEndDate || undefined,
        },
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setOverviewData(res.data);
    } catch (err) {
      console.error("Overview error:", err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        dispatch(brandLogout());
        navigate("/brand-login");
      }
    } finally {
      setOverviewLoading(false);
    }
  }, [activeToken, overviewAppliedStartDate, overviewAppliedEndDate, dispatch, navigate]);

  const fetchImpressions = useCallback(async () => {
    if (!activeToken) return;
    setImpressionsLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/impressions`, {
        params: {
          page: impressionsPage,
          limit: 20,
          sort: impressionsSort,
          startDate: impressionsAppliedStartDate || undefined,
          endDate: impressionsAppliedEndDate || undefined,
        },
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setImpressionsData(res.data.details || []);
      setImpressionsPagination(res.data.pagination);
      setImpressionsTotal(res.data.totalValue || 0);
    } catch (err) {
      console.error("Impressions error:", err);
    } finally {
      setImpressionsLoading(false);
    }
  }, [activeToken, impressionsPage, impressionsSort, impressionsAppliedStartDate, impressionsAppliedEndDate]);

  const fetchProductAnalytics = useCallback(async () => {
    if (!activeToken) return;
    setProductAnalyticsLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/product-analytics`, {
        params: {
          page: productAnalyticsPage,
          limit: 20,
          sort: productAnalyticsSort,
          search: productAnalyticsSearch.trim() || undefined,
          startDate: productAnalyticsAppliedStartDate || undefined,
          endDate: productAnalyticsAppliedEndDate || undefined,
        },
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setProductAnalyticsData(res.data.details || res.data.data || []);
      setProductAnalyticsPagination(res.data.pagination);
      setProductAnalyticsTotal(res.data.pagination?.totalItems || res.data.pagination?.total || 0);
      setProductAnalyticsClicksTotal(res.data.totalValue || 0);
      setProductAnalyticsBuysTotal(res.data.totalBuyClicks || 0);
    } catch (err) {
      console.error("Product analytics error:", err);
    } finally {
      setProductAnalyticsLoading(false);
    }
  }, [
    activeToken,
    productAnalyticsPage,
    productAnalyticsSort,
    productAnalyticsSearch,
    productAnalyticsAppliedStartDate,
    productAnalyticsAppliedEndDate,
  ]);

  const fetchBuyClicks = useCallback(async () => {
    if (!activeToken) return;
    setBuyClicksLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/buy-clicks`, {
        params: {
          page: buyClicksPage,
          limit: 20,
          channel: buyClicksFilter,
          search: buyClicksSearch.trim() || undefined,
          startDate: buyClicksAppliedStartDate || undefined,
          endDate: buyClicksAppliedEndDate || undefined,
        },
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setBuyClicksData(res.data.details || res.data.data || []);
      setBuyClicksPagination(res.data.pagination);
      setBuyClicksTotal(res.data.totalValue || 0);
      setBuyClicksTotalPrice(res.data.totalPriceSum || 0);
      if (res.data.channelCounts) {
        setBuyClicksChannelCounts(res.data.channelCounts);
      }
    } catch (err) {
      console.error("Buy clicks error:", err);
    } finally {
      setBuyClicksLoading(false);
    }
  }, [
    activeToken,
    buyClicksPage,
    buyClicksFilter,
    buyClicksSearch,
    buyClicksAppliedStartDate,
    buyClicksAppliedEndDate,
  ]);

  const fetchSearchQueries = useCallback(async () => {
    if (!activeToken) return;
    setQueriesLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/search-queries`, {
        params: {
          page: queryPage,
          limit: 20,
          startDate: queriesAppliedStartDate || undefined,
          endDate: queriesAppliedEndDate || undefined,
        },
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setSearchQueriesData(res.data.data || []);
      setQueryPagination(res.data.pagination);
      setQueriesTotal(res.data.pagination?.total || 0);
    } catch (err) {
      console.error("Search queries error:", err);
    } finally {
      setQueriesLoading(false);
    }
  }, [activeToken, queryPage, queriesAppliedStartDate, queriesAppliedEndDate]);

  const fetchCatalog = useCallback(async () => {
    if (!activeToken || !isOnboarded) return;
    setCatalogLoading(true);
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/products/catalog`, {
        params: { page: catalogPage, limit: 15 },
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setCatalogData(res.data);
    } catch (err) {
      console.error("Error fetching brand catalog:", err);
    } finally {
      setCatalogLoading(false);
    }
  }, [activeToken, isOnboarded, catalogPage]);

  const fetchStagedPreview = useCallback(async () => {
    if (!activeToken || !isOnboarded) return;
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/products/catalog?staged=true`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      if (res.data.success && res.data.products && res.data.products.length > 0) {
        setStagedProducts(res.data.products);
        setIsStagedMode(true);
      }
    } catch (e) {
      console.error("Error fetching staged preview:", e);
    }
  }, [activeToken, isOnboarded]);

  const fetchCatalogStatus = useCallback(async () => {
    if (!activeToken || !isOnboarded) return;
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/products/status`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      if (res.data.success) {
        const prevStatus = prevCatalogStatusRef.current;
        prevCatalogStatusRef.current = res.data.status;

        if (prevStatus === "processing" && res.data.status === "completed") {
          clearBrandSessionCache(storedBrand?.name);
          setCatalogSyncSuccess(
            `Catalog updated successfully! All products have been indexed into Handpik search engine.`
          );
        }

        setCatalogStatusInfo({
          status: res.data.status,
          updatedAt: res.data.updatedAt,
          error: res.data.error,
          hasStaged: res.data.hasStaged,
          stagedCount: res.data.stagedCount,
        });
        if (res.data.hasStaged && !isStagedMode && stagedProducts.length === 0) {
          fetchStagedPreview();
        }
      }
    } catch (e) {
      console.error("Error fetching catalog status:", e);
    }
  }, [activeToken, isOnboarded, isStagedMode, stagedProducts.length, fetchStagedPreview]);

  useEffect(() => {
    if (activeTab === "overview") fetchOverview();
    if (activeTab === "impressions") fetchImpressions();
    if (activeTab === "product-analytics") fetchProductAnalytics();
    if (activeTab === "buy-clicks") fetchBuyClicks();
    if (activeTab === "search-insights") fetchSearchQueries();
    if (activeTab === "manage-products" && isOnboarded) {
      fetchCatalogStatus();
      fetchCatalog();
    }
  }, [
    activeTab,
    isOnboarded,
    fetchOverview,
    fetchImpressions,
    fetchProductAnalytics,
    fetchBuyClicks,
    fetchSearchQueries,
    fetchCatalog,
    fetchCatalogStatus,
  ]);

  useEffect(() => {
    let interval = null;
    if (activeTab === "manage-products" && isOnboarded && catalogStatusInfo.status === "processing") {
      interval = setInterval(() => {
        fetchCatalogStatus();
        fetchCatalog();
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, isOnboarded, catalogStatusInfo.status, fetchCatalogStatus, fetchCatalog]);

  const handleLogout = () => {
    dispatch(brandLogout());
    navigate("/brand-login");
  };

  const handleFileChange = (file) => {
    if (!file) return;
    setUploadErrors([]);
    setUploadSuccess(null);
    setClientErrors([]);
    setFilePreview(null);

    if (!file.name.toLowerCase().endsWith(".csv")) {
      setClientErrors(["Please select a valid .csv file."]);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);

    // Fast client-side pre-validation
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result;
      if (!text || typeof text !== "string") return;

      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
      if (lines.length < 2) {
        setClientErrors(["The file appears to be empty or has no data rows."]);
        return;
      }

      const headers = lines[0]
        .toLowerCase()
        .split(",")
        .map((h) => h.replace(/[^a-z0-9]/g, "_").trim());

      const allAllowedAliases = {
        sku: ["sku", "sku_code", "article_code", "code", "article_number", "item_code", "product_code"],
        title: ["title", "product_title", "name", "product_name", "item_title", "item_name"],
        description: ["description", "desc", "product_description", "details"],
        original_price: ["original_price", "price", "retail_price", "mrp", "regular_price", "actual_price"],
        sale_price: ["sale_price", "discount_price", "discounted_price", "offer_price", "sale"],
        images_link: ["images_link", "images_links", "image_link", "image_links", "images", "image_url", "image_urls", "image", "photos"],
        category: ["category", "categories", "category_tags", "tags", "collection"],
        size: ["size", "sizes", "available_sizes"],
        color: ["color", "colour", "colors", "shade"],
      };

      const requiredKeys = ["sku", "title", "description", "original_price", "images_link"];

      const missing = [];
      for (const reqKey of requiredKeys) {
        const aliases = allAllowedAliases[reqKey];
        if (!headers.some((h) => aliases.some((a) => h.includes(a)))) {
          missing.push(reqKey.replace("_", " "));
        }
      }

      // Check for unrecognized/invalid headers
      const unrecognized = headers.filter(
        (h) => h.length > 0 && !Object.values(allAllowedAliases).some((aliases) => aliases.some((a) => h.includes(a)))
      );

      const errorsFound = [];
      if (unrecognized.length > 0) {
        errorsFound.push(`Unrecognized column header(s) found: ${unrecognized.map((u) => `'${u}'`).join(", ")}. Allowed columns: sku, title, description, original_price, sale_price, images link, category, size, color.`);
      }
      if (missing.length > 0) {
        errorsFound.push(`Missing required column header(s): ${missing.join(", ")}`);
      }

      if (errorsFound.length > 0) {
        setClientErrors(errorsFound);
      } else {
        setFilePreview({
          rowCount: lines.length - 1,
          fileName: file.name,
          fileSize: `${(file.size / 1024).toFixed(1)} KB`,
        });
      }
    };
    reader.readAsText(file.slice(0, 50000));
  };

  const handleUploadCsv = async (e) => {
    e?.preventDefault();
    if (!selectedFile) {
      setModalConfig({
        title: "No File Selected",
        message: "Please select or drag-and-drop a .csv file to upload your catalog.",
        type: "danger",
        confirmText: "OK",
      });
      return;
    }
    setUploading(true);
    setUploadErrors([]);
    setUploadSuccess(null);

    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      const res = await axios.post(
        `${config.backendUrl}/brand-dashboard/products/upload-csv`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${activeToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setUploadSuccess(res.data.message || `Successfully processed ${res.data.totalCount} products!`);
      if (res.data.products && Array.isArray(res.data.products)) {
        setStagedProducts(res.data.products);
        setIsStagedMode(true);
      }
      setSelectedFile(null);
      setFilePreview(null);
      setShowUploadDropzone(false);
      setCatalogPage(1);
      setCatalogStatusInfo((prev) => ({ ...prev, error: null }));
      setCatalogSyncSuccess(null);
      fetchCatalogStatus();
    } catch (err) {
      const resp = err.response?.data;
      if (resp?.errors && Array.isArray(resp.errors)) {
        setUploadErrors(resp.errors);
      } else {
        setUploadErrors([resp?.message || "Failed to process CSV file."]);
      }
    } finally {
      setUploading(false);
    }
  };

  const executeDiscardStaged = async () => {
    try {
      await axios.delete(`${config.backendUrl}/brand-dashboard/products/discard-staged`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      setStagedProducts([]);
      setIsStagedMode(false);
      setSelectedFile(null);
      setFilePreview(null);
      setUploadSuccess(null);
      fetchCatalogStatus();
      fetchCatalog();
    } catch (err) {
      setModalConfig({
        title: "Discard Failed",
        message: err.response?.data?.message || "Failed to discard staged preview.",
        type: "danger",
        confirmText: "Close",
      });
    }
  };

  const handleDiscardStaged = () => {
    setModalConfig({
      title: "Discard Staged Preview?",
      message: "Are you sure you want to discard this uploaded CSV? Your staged changes will be cleared and the dashboard will return to your live catalog.",
      type: "danger",
      confirmText: "Discard Staged",
      cancelText: "Keep Staged",
      onConfirm: executeDiscardStaged,
      onCancel: true,
    });
  };

  const handleDownloadSample = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/products/sample-template`, {
        headers: { Authorization: `Bearer ${activeToken}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "handpik_sample_catalog_template.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setModalConfig({
        title: "Download Failed",
        message: "Failed to download sample template.",
        type: "danger",
        confirmText: "Close",
      });
    }
  };

  const handleDownloadCurrentCsv = async () => {
    try {
      const res = await axios.get(`${config.backendUrl}/brand-dashboard/products/download-csv`, {
        headers: { Authorization: `Bearer ${activeToken}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${storedBrand?.name?.toLowerCase().replace(/[^a-z0-9]/g, "")}_catalog.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setModalConfig({
        title: "No Active Catalog",
        message: err.response?.data?.message || "No products currently found for this brand.",
        type: "info",
        confirmText: "Close",
      });
    }
  };

  const executeInitiateSync = async () => {
    setSyncing(true);
    try {
      await axios.post(
        `${config.backendUrl}/brand-dashboard/products/sync`,
        {},
        { headers: { Authorization: `Bearer ${activeToken}` } }
      );
      setStagedProducts([]);
      setIsStagedMode(false);
      setUploadSuccess(null);
      setCatalogStatusInfo((prev) => ({ ...prev, status: "processing", error: null }));
      fetchCatalogStatus();
    } catch (err) {
      setModalConfig({
        title: "Sync Failed",
        message: err.response?.data?.message || "Failed to initiate catalog update.",
        type: "danger",
        confirmText: "Close",
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleInitiateSync = () => {
    const count = stagedProducts.length || catalogStatusInfo.stagedCount || catalogData?.count || 0;
    setModalConfig({
      title: "Update Catalog?",
      message: `Ready to initiate catalog update with ${count} products? This will make your new catalog live on Handpik. You can safely close or navigate away from this page while processing continues.`,
      type: "info",
      confirmText: "Update Catalog",
      cancelText: "Cancel",
      onConfirm: executeInitiateSync,
      onCancel: true,
    });
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    setSettingsSuccess(false);

    const cleanCode = settingsCountryCode.replace(/[^0-9]/g, "").trim();
    const cleanNumber = settingsPhoneNumber.replace(/[^0-9]/g, "").trim();
    let fullWhatsapp = null;

    if (cleanCode || cleanNumber) {
      if (!cleanCode) {
        setModalConfig({
          title: "Invalid WhatsApp Number",
          message: "Please enter a country code.",
          type: "danger",
          confirmText: "Close",
        });
        setSettingsSaving(false);
        return;
      }
      if (!cleanNumber) {
        setModalConfig({
          title: "Invalid WhatsApp Number",
          message: "Please enter your phone number.",
          type: "danger",
          confirmText: "Close",
        });
        setSettingsSaving(false);
        return;
      }
      fullWhatsapp = `+${cleanCode}${cleanNumber}`;
      if (!/^\+[0-9]{7,18}$/.test(fullWhatsapp)) {
        setModalConfig({
          title: "Invalid WhatsApp Number",
          message: "WhatsApp number can only contain '+' and numbers.",
          type: "danger",
          confirmText: "Close",
        });
        setSettingsSaving(false);
        return;
      }
    }

    try {
      const res = await axios.put(
        `${config.backendUrl}/brand-auth/me`,
        {
          whatsapp_number: fullWhatsapp,
          instagram_handle: settingsInstagram,
        },
        { headers: { Authorization: `Bearer ${activeToken}` } }
      );
      dispatch(setBrandSession({ token: activeToken, brand: res.data.brand }));
      setSettingsSuccess(true);
      setTimeout(() => setSettingsSuccess(false), 3000);
    } catch (err) {
      setModalConfig({
        title: "Profile Update Failed",
        message: err.response?.data?.message || "Failed to update profile settings.",
        type: "danger",
        confirmText: "Close",
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  // Stat Card theme classes (Matching DashboardStats.jsx in /dashboard)
  const getThemeClasses = (theme) => {
    switch (theme) {
      case "blue": return "bg-blue-50 text-blue-600 ring-1 ring-blue-500/20";
      case "purple": return "bg-purple-50 text-purple-600 ring-1 ring-purple-500/20";
      case "green": return "bg-green-50 text-green-600 ring-1 ring-green-500/20";
      case "indigo": return "bg-indigo-50 text-indigo-600 ring-1 ring-indigo-500/20";
      case "rose": return "bg-rose-50 text-rose-600 ring-1 ring-rose-500/20";
      case "emerald": return "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-500/20";
      default: return "bg-gray-50 text-gray-600 ring-1 ring-gray-500/20";
    }
  };

  const getChannelBadge = (channel) => {
    const ch = (channel || "website").toLowerCase();
    if (ch === "whatsapp") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
          <WhatsAppIcon className="w-3.5 h-3.5" fill="#059669" />
          WhatsApp
        </span>
      );
    }
    if (ch === "instagram") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-pink-50 text-pink-700 border border-pink-200 shadow-2xs">
          <InstagramIcon className="w-3.5 h-3.5" fill="#db2777" />
          Instagram
        </span>
      );
    }
    if (ch === "direct_order") {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200 shadow-2xs">
          <ShoppingBag className="w-3 h-3 text-indigo-600" />
          Direct Order
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200 shadow-2xs">
        <ExternalLink className="w-3 h-3 text-gray-500" />
        Website
      </span>
    );
  };

  const StatCard = ({ title, value, icon: Icon, theme, tab, isString }) => (
    <div
      key={title}
      onClick={() => tab && setActiveTab(tab)}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex flex-col justify-between transition-all duration-300 relative overflow-hidden group ${tab ? "cursor-pointer hover:shadow-lg transform hover:-translate-y-1" : ""
        }`}
    >
      <div
        className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-30 blur-3xl transition-transform duration-700 group-hover:scale-150 ${getThemeClasses(theme).split(" ")[0]
          }`}
      />
      <div className="flex items-center mb-2 relative z-10">
        <div
          className={`p-2 rounded-lg mr-2.5 ${getThemeClasses(
            theme
          )} shadow-sm transition-transform duration-300 group-hover:scale-110`}
        >
          <Icon className="w-4 h-4" />
        </div>
        <h3 className="text-xs font-bold text-gray-500 tracking-wide uppercase">{title}</h3>
      </div>
      <div className="relative z-10">
        <p className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
          {isString ? value : (value ?? 0).toLocaleString()}
        </p>
      </div>
    </div>
  );

  const overviewCards = [
    {
      title: "Total Impressions",
      value: overviewData?.stats?.impressions,
      icon: Eye,
      theme: "purple",
      tab: "impressions",
    },
    {
      title: "Product Clicks",
      value: overviewData?.stats?.productClicks,
      icon: Package,
      theme: "rose",
      tab: "product-analytics",
    },
    {
      title: "Buy Clicks",
      value: overviewData?.stats?.buyClicks,
      icon: ShoppingCart,
      theme: "green",
      tab: "buy-clicks",
    },
    {
      title: "Buy Clicks Value",
      value: `Rs. ${Math.round(overviewData?.stats?.estimatedRevenue || 0).toLocaleString()}`,
      icon: Banknote,
      theme: "emerald",
      isString: true,
    },
  ];

  if (!activeToken || !storedBrand) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <Backdrop open={overviewLoading} className="z-50 text-white">
        <CircularProgress color="inherit" />
      </Backdrop>

      <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
        {/* Top Header Bar (Like Before, with Clean Design) */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src={require("../../assets/images/login-handpik-logo.png")}
                alt="Handpik"
                className="h-8 w-auto object-contain cursor-pointer"
                onClick={() => navigate("/")}
              />
              <div className="h-6 w-px bg-gray-200 hidden sm:block" />
              <div>
                <h1 className="text-lg font-bold text-gray-900 tracking-tight leading-tight">
                  {storedBrand?.name}
                </h1>
                <p className="text-xs text-gray-400 font-medium">Brand Partner Dashboard</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              {hasAdminToken && (
                <button
                  onClick={() => navigate("/dashboard")}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors"
                  title="Return to Master Admin Dashboard"
                >
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Return to Admin
                </button>
              )}

              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-sm"
              >
                <LogOut className="w-3.5 h-3.5" />
                Sign Out
              </button>
            </div>
          </div>

          {/* Horizontal Top Navigation Tabs Bar */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex overflow-x-auto space-x-6 border-t border-gray-100">
            {tabsList.map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`py-3 text-xs sm:text-sm font-semibold flex items-center gap-2 border-b-2 whitespace-nowrap transition-colors ${isActive
                    ? "border-indigo-600 text-indigo-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </header>

        {/* Main Content Area */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1 w-full space-y-6">
          {/* 1. OVERVIEW TAB */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* 4 Stat Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {overviewCards.map(StatCard)}
              </div>

              {/* Overview Floating Date Picker (Exact Style from /dashboard) */}
              <div className="flex justify-center pt-2 pb-2 relative z-20">
                <div className="bg-white p-2.5 rounded-2xl sm:rounded-full shadow-lg shadow-indigo-500/10 border-2 border-indigo-50 flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto mx-4 sm:mx-0">
                  <div className="flex items-center gap-3 w-full sm:w-auto px-2 sm:pl-3">
                    <input
                      type="date"
                      className="flex-1 sm:flex-none bg-indigo-50/50 border border-indigo-100 text-indigo-900 font-medium p-2 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none focus:bg-white transition-all cursor-pointer"
                      value={overviewDraftStartDate}
                      onChange={(e) => setOverviewDraftStartDate(e.target.value)}
                    />
                    <span className="text-indigo-300 font-bold text-xs">to</span>
                    <input
                      type="date"
                      className="flex-1 sm:flex-none bg-indigo-50/50 border border-indigo-100 text-indigo-900 font-medium p-2 rounded-lg text-xs focus:ring-2 focus:ring-indigo-500 focus:outline-none focus:bg-white transition-all cursor-pointer"
                      value={overviewDraftEndDate}
                      onChange={(e) => setOverviewDraftEndDate(e.target.value)}
                    />
                  </div>
                  <div className="flex items-center gap-2 sm:border-l sm:pl-4 border-indigo-100 pr-1 w-full sm:w-auto justify-end mt-2 sm:mt-0">
                    <button
                      onClick={() => {
                        setOverviewAppliedStartDate(overviewDraftStartDate);
                        setOverviewAppliedEndDate(overviewDraftEndDate);
                      }}
                      className="bg-indigo-600 text-white font-semibold px-4 py-1.5 rounded-lg text-xs hover:bg-indigo-700 transition-colors shadow-sm shadow-indigo-200"
                    >
                      Apply
                    </button>
                    {(overviewAppliedStartDate || overviewAppliedEndDate) && (
                      <button
                        onClick={() => {
                          setOverviewDraftStartDate("");
                          setOverviewDraftEndDate("");
                          setOverviewAppliedStartDate("");
                          setOverviewAppliedEndDate("");
                        }}
                        className="text-gray-500 font-medium px-3 py-1.5 rounded-lg text-xs hover:bg-gray-100 transition-colors"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 2. PRODUCT ANALYTICS TAB */}
          {activeTab === "product-analytics" && (
            <div className="space-y-4">
              {/* Top Filter and Search Bar */}
              <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4 space-y-3.5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  {/* Search Input for Title or SKU */}
                  <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-xs bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-gray-400"
                      placeholder="Search by product title or SKU..."
                      value={productAnalyticsSearch}
                      onChange={(e) => {
                        setProductAnalyticsSearch(e.target.value);
                        setProductAnalyticsPage(1);
                      }}
                    />
                    {productAnalyticsSearch && (
                      <button
                        onClick={() => {
                          setProductAnalyticsSearch("");
                          setProductAnalyticsPage(1);
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-100"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Date Filters & Sorting Controls */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Date Range Picker */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">From:</span>
                      <input
                        type="date"
                        className="border border-gray-200 px-2 py-1.5 rounded-lg text-xs bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                        value={productAnalyticsDraftStartDate}
                        onChange={(e) => setProductAnalyticsDraftStartDate(e.target.value)}
                      />
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">To:</span>
                      <input
                        type="date"
                        className="border border-gray-200 px-2 py-1.5 rounded-lg text-xs bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                        value={productAnalyticsDraftEndDate}
                        onChange={(e) => setProductAnalyticsDraftEndDate(e.target.value)}
                      />
                      <button
                        className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors text-xs shadow-xs"
                        onClick={() => {
                          setProductAnalyticsPage(1);
                          setProductAnalyticsAppliedStartDate(productAnalyticsDraftStartDate);
                          setProductAnalyticsAppliedEndDate(productAnalyticsDraftEndDate);
                        }}
                      >
                        Apply
                      </button>
                      {(productAnalyticsAppliedStartDate || productAnalyticsAppliedEndDate) && (
                        <button
                          className="bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-xs"
                          onClick={() => {
                            setProductAnalyticsDraftStartDate("");
                            setProductAnalyticsDraftEndDate("");
                            setProductAnalyticsAppliedStartDate("");
                            setProductAnalyticsAppliedEndDate("");
                            setProductAnalyticsPage(1);
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <div className="h-6 w-px bg-gray-200 hidden sm:block" />

                    {/* Sorting Dropdown */}
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">Sort:</span>
                      <select
                        className="border border-gray-200 px-2.5 py-1.5 rounded-lg text-xs bg-white text-gray-700 font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 cursor-pointer"
                        value={productAnalyticsSort}
                        onChange={(e) => {
                          setProductAnalyticsPage(1);
                          setProductAnalyticsSort(e.target.value);
                        }}
                      >
                        <option value="latest_product_click">Latest Product Click</option>
                        <option value="latest_buy_click">Latest Buy Click</option>
                        <option value="most_product_clicks">Most Product Clicks</option>
                        <option value="most_buy_clicks">Most Buy Clicks</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Summary metrics bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 text-xs">
                  <span className="text-gray-500 font-medium">
                    <span className="font-bold text-gray-900">{productAnalyticsTotal.toLocaleString()}</span> Products
                  </span>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-50 text-blue-800 font-semibold border border-blue-200/60">
                      <Package className="w-3.5 h-3.5 text-blue-600" />
                      Total Product Clicks: {productAnalyticsClicksTotal.toLocaleString()}
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 font-semibold border border-emerald-200/60">
                      <ShoppingCart className="w-3.5 h-3.5 text-emerald-600" />
                      Total Buy Clicks: {productAnalyticsBuysTotal.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Products List / Cards */}
              <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
                {productAnalyticsLoading ? (
                  <div className="p-16 flex justify-center items-center text-indigo-600">
                    <CircularProgress size={30} />
                  </div>
                ) : productAnalyticsData.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h4 className="text-sm font-bold text-gray-800 mb-1">No Products Found</h4>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                      {productAnalyticsSearch
                        ? `No products matched "${productAnalyticsSearch}". Try searching with a different SKU or title.`
                        : "No product clicks or activity recorded for this brand during the selected period."}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {productAnalyticsData.map((item, idx) => {
                      const isExpanded = !!expandedProductIds[item.product_id || idx];
                      const itemUrl =
                        item.product_url ||
                        (storedBrand?.name && item.product_id && item.product_id !== "Unknown"
                          ? `https://handpik.ai/onboardedbrands/${storedBrand.name.toLowerCase().replace(/[^a-z0-9]/g, "")}/${String(item.product_id).toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-")}`
                          : null);

                      return (
                        <div
                          key={item.product_id || idx}
                          className="p-4 sm:p-5 flex flex-col lg:flex-row items-start justify-between gap-4 hover:bg-gray-50/70 transition-colors"
                        >
                          {/* Left: Thumbnail & Details */}
                          <div className="flex items-start gap-4 min-w-0 flex-1">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0 shadow-xs relative group">
                              {item.product_image ? (
                                <img
                                  src={item.product_image}
                                  alt={item.product_title || "Product"}
                                  referrerPolicy="no-referrer"
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              ) : (
                                <Package className="w-8 h-8 m-auto text-gray-400" />
                              )}
                            </div>

                            <div className="min-w-0 flex-1 space-y-1">
                              {/* SKU on top of product title */}
                              <div className="font-mono text-[11px] font-semibold text-gray-800">
                                SKU: {item.product_id || "N/A"}
                              </div>

                              <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug line-clamp-2">
                                {item.product_title || "Unknown Product"}
                              </h3>

                              <div className="flex flex-wrap items-center gap-2 text-xs pt-0.5">
                                {item.product_price && (
                                  <span className="font-bold text-gray-900 bg-emerald-50 text-emerald-800 px-2 py-0.5 rounded border border-emerald-200/60">
                                    {item.currency || "Rs."} {parseFloat(item.product_price).toLocaleString()}
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-3 pt-1 text-[11px] text-gray-600">
                                {item.latest_product_click && (
                                  <span>Last Product Click: {formatTimestamp(item.latest_product_click)}</span>
                                )}
                                {item.latest_buy_click && (
                                  <span>Last Buy Click: {formatTimestamp(item.latest_buy_click)}</span>
                                )}
                              </div>

                              {itemUrl && (
                                <div className="pt-1">
                                  <a
                                    href={itemUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-900 border border-indigo-200 text-[11px] font-semibold transition-colors shadow-2xs"
                                  >
                                    View Product <ExternalLink className="w-3 h-3" />
                                  </a>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right: Badges & Expandable Breakdown (Top-aligned, slim, non-expanding row) */}
                          <div className="flex flex-col sm:items-end gap-1.5 flex-shrink-0 w-full lg:w-auto relative">
                            <div className="flex flex-wrap items-center gap-2">
                              {/* Product Clicks Badge */}
                              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 shadow-2xs">
                                <Package className="w-3.5 h-3.5 text-blue-600" />
                                <span>{item.product_clicks || 0} Product Clicks</span>
                              </div>

                              {/* Buy Clicks Badge (Expandable only for onboarded brands) */}
                              {isOnboarded ? (
                                <button
                                  onClick={() => toggleExpandProduct(item.product_id || idx)}
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all shadow-2xs cursor-pointer select-none ${isExpanded
                                    ? "bg-emerald-600 text-white border-emerald-600 ring-2 ring-emerald-500/20"
                                    : "bg-emerald-50 text-emerald-800 border-emerald-200 hover:bg-emerald-100"
                                    }`}
                                  title="Click to view breakdown by source"
                                >
                                  <ShoppingCart className={`w-3.5 h-3.5 ${isExpanded ? "text-white" : "text-emerald-600"}`} />
                                  <span>{item.buy_clicks || 0} Buy Clicks</span>
                                  {isExpanded ? (
                                    <ChevronUp className="w-3.5 h-3.5 ml-0.5" />
                                  ) : (
                                    <ChevronDown className="w-3.5 h-3.5 ml-0.5" />
                                  )}
                                </button>
                              ) : (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 shadow-2xs">
                                  <ShoppingCart className="w-3.5 h-3.5 text-emerald-600" />
                                  <span>{item.buy_clicks || 0} Buy Clicks</span>
                                </div>
                              )}
                            </div>

                            {/* Slim Expandable Breakdown Drawer: Floating, Single Line with all 3 sources, no row expansion (Onboarded only) */}
                            {isOnboarded && isExpanded && (
                              <div className="absolute right-0 top-full mt-1.5 z-20 bg-white border border-emerald-200 rounded-xl p-1.5 shadow-lg shadow-emerald-900/10 animate-fadeIn">
                                <div className="flex items-center gap-1.5 whitespace-nowrap text-xs">
                                  {/* Direct Orders */}
                                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50/80 text-indigo-900 rounded-lg border border-indigo-100 shadow-2xs">
                                    <ShoppingBag className="w-3.5 h-3.5 text-indigo-600" />
                                    <span className="font-medium text-[11px]">Direct Orders:</span>
                                    <span className="font-bold text-xs">{item.direct_order_clicks || 0}</span>
                                  </div>

                                  {/* WhatsApp */}
                                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50/80 text-emerald-900 rounded-lg border border-emerald-100 shadow-2xs">
                                    <WhatsAppIcon className="w-3.5 h-3.5" fill="#059669" />
                                    <span className="font-medium text-[11px]">WhatsApp:</span>
                                    <span className="font-bold text-xs">{item.whatsapp_clicks || 0}</span>
                                  </div>

                                  {/* Instagram */}
                                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-pink-50/80 text-pink-900 rounded-lg border border-pink-100 shadow-2xs">
                                    <InstagramIcon className="w-3.5 h-3.5" fill="#db2777" />
                                    <span className="font-medium text-[11px]">Instagram:</span>
                                    <span className="font-bold text-xs">{item.instagram_clicks || 0}</span>
                                  </div>

                                  {/* Website (if applicable) */}
                                  {(item.website_clicks > 0 || (!item.direct_order_clicks && !item.whatsapp_clicks && !item.instagram_clicks && item.buy_clicks > 0)) && (
                                    <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 text-gray-800 rounded-lg border border-gray-200 shadow-2xs">
                                      <ExternalLink className="w-3 h-3 text-gray-500" />
                                      <span className="font-medium text-[11px]">Website:</span>
                                      <span className="font-bold text-xs">{item.website_clicks || (item.buy_clicks - ((item.direct_order_clicks || 0) + (item.whatsapp_clicks || 0) + (item.instagram_clicks || 0)))}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {productAnalyticsPagination && productAnalyticsPagination.totalPages > 1 && (
                  <Pagination pagination={productAnalyticsPagination} onPageChange={setProductAnalyticsPage} />
                )}
              </div>
            </div>
          )}

          {/* 4. BUY CLICKS TAB */}
          {activeTab === "buy-clicks" && (
            <div className="space-y-4">
              {/* Filter Buttons & Controls Card */}
              <div className="bg-white rounded-xl shadow-xs border border-gray-200 p-4 space-y-4">
                {/* Clickable Channel Filter Buttons at Top (Only for onboarded brands) */}
                {isOnboarded && (
                  <div className="flex flex-wrap items-center gap-2 pb-3 border-b border-gray-100">
                    {[
                      { key: "all", label: "All", count: buyClicksChannelCounts.all, icon: null },
                      { key: "direct_order", label: "Direct Orders", count: buyClicksChannelCounts.direct_order, icon: ShoppingBag },
                      { key: "whatsapp", label: "WhatsApp", count: buyClicksChannelCounts.whatsapp, icon: "whatsapp" },
                      { key: "instagram", label: "Instagram", count: buyClicksChannelCounts.instagram, icon: "instagram" },
                    ].map((tab) => {
                      const isActive = buyClicksFilter === tab.key;
                      return (
                        <button
                          key={tab.key}
                          onClick={() => {
                            setBuyClicksFilter(tab.key);
                            setBuyClicksPage(1);
                          }}
                          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all shadow-xs cursor-pointer ${isActive
                            ? "bg-indigo-600 text-white ring-2 ring-indigo-500/20 shadow-indigo-100"
                            : "bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200"
                            }`}
                        >
                          {tab.icon === "whatsapp" ? (
                            <WhatsAppIcon className="w-3.5 h-3.5" fill={isActive ? "#ffffff" : "#25D366"} />
                          ) : tab.icon === "instagram" ? (
                            <InstagramIcon className="w-3.5 h-3.5" fill={isActive ? "#ffffff" : "#E1306C"} />
                          ) : tab.icon ? (
                            <tab.icon className={`w-3.5 h-3.5 ${isActive ? "text-white" : "text-gray-500"}`} />
                          ) : null}
                          <span>{tab.label}</span>
                          <span
                            className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${isActive ? "bg-white/20 text-white" : "bg-gray-200 text-gray-800"
                              }`}
                          >
                            {(tab.count ?? 0).toLocaleString()}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Search and Date Range Toolbar */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  {/* Search Input for SKU or Title */}
                  <div className="relative flex-1 min-w-[240px]">
                    <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      type="text"
                      className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-xs bg-gray-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all placeholder:text-gray-400"
                      placeholder="Search by product title or SKU..."
                      value={buyClicksSearch}
                      onChange={(e) => {
                        setBuyClicksSearch(e.target.value);
                        setBuyClicksPage(1);
                      }}
                    />
                    {buyClicksSearch && (
                      <button
                        onClick={() => {
                          setBuyClicksSearch("");
                          setBuyClicksPage(1);
                        }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full hover:bg-gray-100"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  {/* Date Range Picker */}
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">From:</span>
                    <input
                      type="date"
                      className="border border-gray-200 px-2 py-1.5 rounded-lg text-xs bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      value={buyClicksDraftStartDate}
                      onChange={(e) => setBuyClicksDraftStartDate(e.target.value)}
                    />
                    <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">To:</span>
                    <input
                      type="date"
                      className="border border-gray-200 px-2 py-1.5 rounded-lg text-xs bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                      value={buyClicksDraftEndDate}
                      onChange={(e) => setBuyClicksDraftEndDate(e.target.value)}
                    />
                    <button
                      className="bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors text-xs shadow-xs cursor-pointer"
                      onClick={() => {
                        setBuyClicksPage(1);
                        setBuyClicksAppliedStartDate(buyClicksDraftStartDate);
                        setBuyClicksAppliedEndDate(buyClicksDraftEndDate);
                      }}
                    >
                      Apply
                    </button>
                    {(buyClicksAppliedStartDate || buyClicksAppliedEndDate) && (
                      <button
                        className="bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg font-medium hover:bg-gray-200 transition-colors text-xs cursor-pointer"
                        onClick={() => {
                          setBuyClicksDraftStartDate("");
                          setBuyClicksDraftEndDate("");
                          setBuyClicksAppliedStartDate("");
                          setBuyClicksAppliedEndDate("");
                          setBuyClicksPage(1);
                        }}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* Summary stats line */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-gray-100 text-xs">
                  <span className="text-gray-500 font-medium">
                    <span className="font-bold text-gray-900">{buyClicksTotal.toLocaleString()}</span> Buy Clicks
                  </span>
                  {buyClicksTotalPrice > 0 && (
                    <span className="text-gray-700 font-semibold bg-emerald-50 text-emerald-800 px-2.5 py-1 rounded-md border border-emerald-200/60">
                      Total Price Sum: Rs. {Math.round(buyClicksTotalPrice).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>

              {/* Tabular Layout (Columns for each product detail) */}
              <div className="bg-white rounded-xl shadow-xs border border-gray-200 overflow-hidden">
                {buyClicksLoading ? (
                  <div className="p-16 flex justify-center items-center text-indigo-600">
                    <CircularProgress size={30} />
                  </div>
                ) : buyClicksData.length === 0 ? (
                  <div className="text-center py-16 px-4">
                    <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <h4 className="text-sm font-bold text-gray-800 mb-1">No Buy Clicks Found</h4>
                    <p className="text-xs text-gray-500 max-w-sm mx-auto">
                      {buyClicksFilter !== "all"
                        ? `No ${buyClicksFilter.replace(/_/g, " ")} events found for the selected period.`
                        : "No buy clicks or purchase inquiries recorded for this brand during the selected period."}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto w-full">
                    <table className="w-full text-left border-collapse min-w-[700px]">
                      <thead>
                        <tr className="bg-gray-50/80 border-b border-gray-200 text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                          <th className="py-3 px-4">Date & Time</th>
                          <th className="py-3 px-4">Product Details</th>
                          <th className="py-3 px-4">Price</th>
                          <th className="py-3 px-4">Source Channel</th>
                          {isOnboarded && <th className="py-3 px-4 text-center">Actions</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 text-xs">
                        {buyClicksData.map((row, idx) => {
                          const isDirectOrder = (row.channel || "").toLowerCase() === "direct_order";
                          const rowUrl =
                            row.product_url ||
                            (storedBrand?.name && row.product_id && row.product_id !== "Unknown"
                              ? `https://handpik.ai/onboardedbrands/${storedBrand.name.toLowerCase().replace(/[^a-z0-9]/g, "")}/${String(row.product_id).toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-")}`
                              : null);

                          return (
                            <tr key={row.id || idx} className="hover:bg-gray-50/70 transition-colors">
                              {/* 1. Date & Time */}
                              <td className="py-3.5 px-4 whitespace-nowrap text-gray-600">
                                <div className="font-medium text-gray-900">{formatTimestamp(row.click_date).split(",")[0]}</div>
                                <div className="text-[11px] text-gray-400">{formatTimestamp(row.click_date).split(",")[1] || ""}</div>
                              </td>

                              {/* 2. Product Details */}
                              <td className="py-3.5 px-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-16 h-16 bg-gray-100 rounded-xl border border-gray-200 overflow-hidden flex-shrink-0">
                                    {row.product_image ? (
                                      <img
                                        src={row.product_image}
                                        alt={row.product_title || "Product"}
                                        referrerPolicy="no-referrer"
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = "none";
                                        }}
                                      />
                                    ) : (
                                      <Package className="w-7 h-7 m-auto text-gray-400" />
                                    )}
                                  </div>
                                  <div className="min-w-0 max-w-xs space-y-1">
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-mono text-xs font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded border border-gray-300">
                                        SKU: {row.product_id || "N/A"}
                                      </span>
                                      {rowUrl && (
                                        <a
                                          href={rowUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                                          title="View Product"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                        </a>
                                      )}
                                    </div>
                                    <div className="font-bold text-gray-900 truncate text-xs sm:text-sm" title={row.product_title}>
                                      {row.product_title || "Unknown Product"}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* 3. Price */}
                              <td className="py-3.5 px-4 whitespace-nowrap">
                                {row.product_price ? (
                                  <span className="font-bold text-gray-900">
                                    {row.currency || "PKR"} {parseFloat(row.product_price).toLocaleString()}
                                  </span>
                                ) : (
                                  <span className="text-gray-400 font-mono">-</span>
                                )}
                              </td>

                              {/* 4. Channel / Source */}
                              <td className="py-3.5 px-4 whitespace-nowrap">
                                {getChannelBadge(row.channel)}
                              </td>

                              {/* 5. Actions: Eye icon for Direct Orders (Onboarded only) */}
                              {isOnboarded && (
                                <td className="py-3.5 px-4 text-center whitespace-nowrap">
                                  {isDirectOrder ? (
                                    <button
                                      onClick={() => setSelectedDirectOrder(row)}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-900 border border-indigo-200 text-xs font-semibold transition-all shadow-xs cursor-pointer group"
                                      title="View Direct Order Details"
                                    >
                                      <Eye className="w-3.5 h-3.5 transition-transform group-hover:scale-110" />
                                      <span>Order Details</span>
                                    </button>
                                  ) : (
                                    <span className="text-gray-300 font-mono text-xs">-</span>
                                  )}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {buyClicksPagination && buyClicksPagination.totalPages > 1 && (
                  <Pagination pagination={buyClicksPagination} onPageChange={setBuyClicksPage} />
                )}
              </div>
            </div>
          )}

          {/* 4. IMPRESSIONS TAB */}
          {activeTab === "impressions" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b flex flex-wrap gap-4 items-center bg-gray-50 rounded-t-lg justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600">From:</span>
                  <input
                    type="date"
                    className="border p-1.5 rounded text-xs bg-white"
                    value={impressionsDraftStartDate}
                    onChange={(e) => setImpressionsDraftStartDate(e.target.value)}
                  />
                  <span className="text-xs font-semibold text-gray-600">To:</span>
                  <input
                    type="date"
                    className="border p-1.5 rounded text-xs bg-white"
                    value={impressionsDraftEndDate}
                    onChange={(e) => setImpressionsDraftEndDate(e.target.value)}
                  />
                  <button
                    className="bg-indigo-600 text-white px-3.5 py-1.5 rounded font-semibold hover:bg-indigo-700 transition-colors text-xs"
                    onClick={() => {
                      setImpressionsPage(1);
                      setImpressionsAppliedStartDate(impressionsDraftStartDate);
                      setImpressionsAppliedEndDate(impressionsDraftEndDate);
                    }}
                  >
                    Apply
                  </button>
                  {(impressionsAppliedStartDate || impressionsAppliedEndDate) && (
                    <button
                      className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded font-medium hover:bg-gray-200 transition-colors text-xs"
                      onClick={() => {
                        setImpressionsDraftStartDate("");
                        setImpressionsDraftEndDate("");
                        setImpressionsAppliedStartDate("");
                        setImpressionsAppliedEndDate("");
                        setImpressionsPage(1);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-600">Sort by:</span>
                  <select
                    className="border p-1.5 rounded text-xs bg-white"
                    value={impressionsSort}
                    onChange={(e) => {
                      setImpressionsPage(1);
                      setImpressionsSort(e.target.value);
                    }}
                  >
                    <option value="latest">Latest Impression</option>
                    <option value="most_impressions">Most Impressions</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                <span className="text-xs font-semibold text-gray-700">
                  Total Impressions: {impressionsTotal.toLocaleString()}
                </span>
              </div>

              {impressionsLoading ? (
                <div className="p-12 flex justify-center text-indigo-600 animate-pulse">
                  <CircularProgress size={28} />
                </div>
              ) : impressionsData.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-xs">
                  No impressions found for this brand in the selected period.
                </div>
              ) : (
                <div className="flex flex-col divide-y divide-gray-100">
                  <div className="flex justify-between px-6 py-2.5 bg-gray-100/70 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <span>Source / Discovery Context</span>
                    <span>Impressions</span>
                  </div>
                  {impressionsData.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-4 flex flex-col md:flex-row md:items-center items-start gap-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                        {item.source === "text_search" ? (
                          <Search className="w-5 h-5 text-purple-600" />
                        ) : item.source === "image_search_similar" ? (
                          <ImageIcon className="w-5 h-5 text-purple-600" />
                        ) : item.source === "new_arrivals_page" ? (
                          <Sparkles className="w-5 h-5 text-purple-600" />
                        ) : item.source === "on_sale_page" ? (
                          <Tag className="w-5 h-5 text-purple-600" />
                        ) : item.source === "view_all_page" ? (
                          <Grid className="w-5 h-5 text-purple-600" />
                        ) : item.source === "similar_products" ? (
                          <Layers className="w-5 h-5 text-purple-600" />
                        ) : (
                          <Package className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-gray-900 mb-1 capitalize flex items-center gap-1">
                          {item.source?.replace(/_/g, " ") || "Unknown Source"}
                        </div>
                        {item.source === "text_search" && (item.query || item.category) && (
                          <div className="flex flex-wrap gap-2 text-xs mt-2">
                            {item.query && (
                              <span className="bg-white text-gray-900 px-2.5 py-1 rounded border shadow-sm flex items-center gap-1">
                                <span className="text-gray-500 font-normal">Query:</span>{" "}
                                <span className="font-semibold">
                                  "{item.query}" {item.gender && `(${item.gender})`}
                                </span>
                              </span>
                            )}
                            {item.category && (
                              <span className="bg-white text-gray-900 px-2.5 py-1 rounded border shadow-sm flex items-center gap-1">
                                <span className="text-gray-500 font-normal">Category Tab:</span>{" "}
                                <span className="font-semibold">{item.category}</span>
                              </span>
                            )}
                          </div>
                        )}
                        <div className="text-[11px] text-gray-400 mt-1">
                          Latest impression: {formatTimestamp(item.latest_impression)}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 mt-2 md:mt-0">
                        <span className="text-xs font-semibold bg-purple-100 text-purple-800 px-3 py-1 rounded-full whitespace-nowrap">
                          {item.count} {item.count === 1 ? "Impression" : "Impressions"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {impressionsPagination && impressionsPagination.totalPages > 1 && (
                <Pagination pagination={impressionsPagination} onPageChange={setImpressionsPage} />
              )}
            </div>
          )}

          {/* 5. SEARCH INSIGHTS TAB */}
          {activeTab === "search-insights" && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-4 border-b flex flex-wrap gap-4 items-center bg-gray-50 rounded-t-lg justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-semibold text-gray-600">From:</span>
                  <input
                    type="date"
                    className="border p-1.5 rounded text-xs bg-white"
                    value={queriesDraftStartDate}
                    onChange={(e) => setQueriesDraftStartDate(e.target.value)}
                  />
                  <span className="text-xs font-semibold text-gray-600">To:</span>
                  <input
                    type="date"
                    className="border p-1.5 rounded text-xs bg-white"
                    value={queriesDraftEndDate}
                    onChange={(e) => setQueriesDraftEndDate(e.target.value)}
                  />
                  <button
                    className="bg-indigo-600 text-white px-3.5 py-1.5 rounded font-semibold hover:bg-indigo-700 transition-colors text-xs"
                    onClick={() => {
                      setQueryPage(1);
                      setQueriesAppliedStartDate(queriesDraftStartDate);
                      setQueriesAppliedEndDate(queriesDraftEndDate);
                    }}
                  >
                    Apply
                  </button>
                  {(queriesAppliedStartDate || queriesAppliedEndDate) && (
                    <button
                      className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded font-medium hover:bg-gray-200 transition-colors text-xs"
                      onClick={() => {
                        setQueriesDraftStartDate("");
                        setQueriesDraftEndDate("");
                        setQueriesAppliedStartDate("");
                        setQueriesAppliedEndDate("");
                        setQueryPage(1);
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="flex justify-end px-4 py-2 border-b bg-gray-50">
                <span className="text-xs font-semibold text-gray-700">
                  Total Searches: {queriesTotal.toLocaleString()}
                </span>
              </div>

              {queriesLoading ? (
                <div className="p-12 flex justify-center text-indigo-600 animate-pulse">
                  <CircularProgress size={28} />
                </div>
              ) : searchQueriesData.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-xs">
                  No customer search queries found leading to your brand.
                </div>
              ) : (
                <div className="w-full divide-y divide-gray-100">
                  {searchQueriesData.map((q, idx) => (
                    <div key={idx} className="p-4 flex justify-between items-center hover:bg-gray-50 transition-colors">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                        <b className="text-xs text-gray-900">{q.query}</b>
                        {q.category && (
                          <span className="text-gray-500 text-[11px] bg-gray-100 px-2 py-0.5 rounded">
                            {q.category}
                          </span>
                        )}
                        {q.gender && (
                          <span className="text-gray-500 text-[11px] bg-gray-100 px-2 py-0.5 rounded">
                            {q.gender}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-semibold bg-indigo-100 text-indigo-800 px-2.5 py-1 rounded">
                        {q.count} {q.count === 1 ? "search" : "searches"}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {queryPagination && queryPagination.totalPages > 1 && (
                <Pagination pagination={queryPagination} onPageChange={setQueryPage} />
              )}
            </div>
          )}

          {/* 6. MANAGE PRODUCTS TAB (Onboarded Brands Only) */}
          {activeTab === "manage-products" && isOnboarded && (
            <div className="space-y-6">
              {/* Header Card */}
              <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-50 text-emerald-700 rounded-xl">
                    <FileSpreadsheet className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Product Catalog Management</h2>
                    <p className="text-xs text-gray-500">
                      Upload and manage your product inventory spreadsheet
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {catalogSubView === "guide" ? (
                    <>
                      <button
                        onClick={() => setCatalogSubView("catalog")}
                        className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                      >
                        <ArrowLeft className="w-3.5 h-3.5" />
                        Back to Products Catalog
                      </button>

                      <button
                        onClick={handleDownloadSample}
                        className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                      >
                        <Download className="w-3.5 h-3.5" />
                        Download Sample Template
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setCatalogSubView("guide")}
                        className="px-3.5 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                      >
                        <FileText className="w-3.5 h-3.5" />
                        Sample Template & Instructions
                      </button>

                      {catalogData?.hasCatalog && (
                        <button
                          onClick={handleDownloadCurrentCsv}
                          className="px-3.5 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors border border-gray-200"
                          title="Download latest uploaded CSV"
                        >
                          <Download className="w-3.5 h-3.5 text-gray-500" />
                          Download Current CSV
                        </button>
                      )}

                      {catalogData?.hasCatalog && !isStagedMode && !showUploadDropzone && (
                        <button
                          onClick={() => {
                            setShowUploadDropzone(true);
                            setSelectedFile(null);
                            setFilePreview(null);
                            setUploadErrors([]);
                            setClientErrors([]);
                          }}
                          className="px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          Upload CSV
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* DETAILED CSV GUIDE VIEW */}
              {catalogSubView === "guide" ? (
                <div className="space-y-6">
                  {/* Compact Guidelines Section */}
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3 text-xs">
                    <h3 className="text-sm font-bold text-gray-900 border-b border-gray-100 pb-2">
                      Important Spreadsheet Guidelines
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* 1. SKU - Color Coded Green */}
                      <div className="p-3.5 rounded-xl border border-emerald-200 bg-emerald-50/50 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 font-bold text-emerald-800 text-xs mb-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                            <span>1. SKU (Product ID)</span>
                          </div>
                          <ul className="space-y-1.5 text-gray-700 pl-3.5 list-disc marker:text-emerald-500 text-[11px] leading-relaxed">
                            <li>
                              <strong className="text-gray-900">SKUs can be in any format you prefer:</strong> Alphanumeric, letters, or plain numbers (e.g. <code className="text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded font-mono font-semibold">XYZ-123</code>, <code className="text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded font-mono font-semibold">AB01</code>, <code className="text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded font-mono font-semibold">101</code>, <code className="text-emerald-700 bg-emerald-100 px-1 py-0.5 rounded font-mono font-semibold">A-01</code>).
                            </li>
                            <li>
                              <strong className="text-gray-900">Must be Unique for every product:</strong> No two products can share the same SKU.
                            </li>
                            <li>
                              <strong className="text-gray-900">Keep same SKU across future updates:</strong> Handpik uses it to track your product's analytics. Changing a SKU will treat the item as a brand new product.
                            </li>
                          </ul>
                        </div>
                      </div>

                      {/* 2. Comma-Separated Columns - Color Coded Blue */}
                      <div className="p-3.5 rounded-xl border border-blue-200 bg-blue-50/50 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 font-bold text-blue-800 text-xs mb-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                            <span>2. Comma-Separated Columns</span>
                          </div>
                          <p className="text-gray-700 text-[11px] mb-2 leading-relaxed">
                            If you want to enter multiple values in a single field (such as multiple images, categories, sizes, or colors), simply separate each value using a comma <code className="text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded font-mono font-bold">,</code>
                          </p>
                          <div className="space-y-1 text-[11px]">
                            <span className="font-semibold text-gray-900 block">Columns that support multiple values:</span>
                            <div className="flex flex-wrap gap-1.5">
                              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono font-medium text-[10px]">
                                images link
                              </span>
                              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono font-medium text-[10px]">
                                category
                              </span>
                              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono font-medium text-[10px]">
                                size
                              </span>
                              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono font-medium text-[10px]">
                                color
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* 3. Google Drive Shareable Link - Color Coded Yellow/Amber */}
                      <div className="p-3.5 rounded-xl border border-amber-200 bg-amber-50/50 flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-1.5 font-bold text-amber-800 text-xs mb-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                            <span>3. Google Drive Image Links</span>
                          </div>
                          <ul className="space-y-1.5 text-gray-700 pl-3.5 list-disc marker:text-amber-500 text-[11px] leading-relaxed">
                            <li>
                              Upload image to Google Drive.
                            </li>
                            <li>
                              Right-click image in Google Drive &rarr; click <strong>Share</strong>.
                            </li>
                            <li>
                              Under <em>General access</em>, change from <strong>Restricted</strong> to <strong>Anyone with the link can view</strong>.
                            </li>
                            <li>
                              Click <strong>Copy link</strong> and paste into the <code className="text-amber-900 bg-amber-100 px-1 py-0.5 rounded font-mono font-semibold">images link</code> column.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Complete 9 Columns Specification Table */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-3">
                    <h3 className="text-base font-bold text-gray-900">All 9 Columns Reference</h3>
                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                      <table className="min-w-full divide-y divide-gray-200 text-left text-xs">
                        <thead className="bg-gray-50 text-gray-700 font-semibold uppercase">
                          <tr>
                            <th className="px-4 py-3">Column Name</th>
                            <th className="px-4 py-3">Requirement</th>
                            <th className="px-4 py-3">Allowed Formats & Examples</th>
                            <th className="px-4 py-3">Description & Usage</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 text-gray-600">
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">sku</td>
                            <td className="px-4 py-2.5">
                              <span className="text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded">Required</span>
                            </td>
                            <td className="px-4 py-2.5 font-mono"><code>Any Format</code> / <code>XYZ-123</code> / <code>AB01</code> / <code>101</code> / <code>A-01</code></td>
                            <td className="px-4 py-2.5">
                              <span className="text-emerald-700 font-medium">Unique ID code per item. Permanent identifier for tracking statistics and clicks.</span>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">title</td>
                            <td className="px-4 py-2.5">
                              <span className="text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded">Required</span>
                            </td>
                            <td className="px-4 py-2.5"><code>Blue Embroidered Kurta</code></td>
                            <td className="px-4 py-2.5">Display name of the product shown to shoppers on search and product pages.</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">description</td>
                            <td className="px-4 py-2.5">
                              <span className="text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded">Required</span>
                            </td>
                            <td className="px-4 py-2.5">Textual description of the product</td>
                            <td className="px-4 py-2.5">Detailed description. Line breaks and quotation marks supported.</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">original_price</td>
                            <td className="px-4 py-2.5">
                              <span className="text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded">Required</span>
                            </td>
                            <td className="px-4 py-2.5 font-mono"><code>6500</code> / <code>6500.50</code> / <code>6,500</code></td>
                            <td className="px-4 py-2.5">Regular retail price in PKR. Decimals, commas, and quotation marks supported</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">sale_price</td>
                            <td className="px-4 py-2.5">
                              <span className="text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded">Optional</span>
                            </td>
                            <td className="px-4 py-2.5 font-mono"><code>5200</code> / <code>5200.00</code> / BLANK</td>
                            <td className="px-4 py-2.5">Discounted sale price in PKR (must be &lt; original price). Leave blank if not on sale.</td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">images link</td>
                            <td className="px-4 py-2.5">
                              <span className="text-red-600 font-bold bg-red-50 px-2 py-0.5 rounded">Required</span>
                            </td>
                            <td className="px-4 py-2.5">Public image URL</td>
                            <td className="px-4 py-2.5">
                              <span className="">Publicly viewable link for images or<span className="text-amber-700 font-semibold px-1 py-0.5">Google Drive link</span> </span> <span className="text-blue-600 font-bold px-1.5 py-0.5">Comma-separated column</span>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">category</td>
                            <td className="px-4 py-2.5">
                              <span className="text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded">Optional</span>
                            </td>
                            <td className="px-4 py-2.5 font-mono"><code>Pret, Kurta, Embroidered</code> / BLANK </td>
                            <td className="px-4 py-2.5">
                              Applicable category tags. Leave blank if not applicable. <span className="text-blue-600 font-bold px-1.5 py-0.5">Comma-separated column</span>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">size</td>
                            <td className="px-4 py-2.5">
                              <span className="text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded">Optional</span>
                            </td>
                            <td className="px-4 py-2.5 font-mono"><code>XS, S, M, L, XL </code> / <code>Small, Medium, Large </code> / BLANK</td>
                            <td className="px-4 py-2.5">
                              Available sizes in stock. Leave blank if not applicable. <span className="text-blue-600 font-bold px-1.5 py-0.5">Comma-separated column</span>
                            </td>
                          </tr>
                          <tr>
                            <td className="px-4 py-2.5 font-mono font-bold text-gray-900">color</td>
                            <td className="px-4 py-2.5">
                              <span className="text-gray-500 font-medium bg-gray-100 px-2 py-0.5 rounded">Optional</span>
                            </td>
                            <td className="px-4 py-2.5 font-mono"><code>Blue, Black, Red </code> / BLANK</td>
                            <td className="px-4 py-2.5">
                              Available color(s) of product. Leave blank if not applicable. <span className="text-blue-600 font-bold px-1.5 py-0.5">Comma-separated column</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                /* MAIN CATALOG & UPLOADER VIEW */
                <>
                  {/* Persistent Processing Banner (Always on top) */}
                  {catalogStatusInfo.status === "processing" && (
                    <div className="p-4 rounded-xl bg-blue-50 border border-blue-200 text-blue-950 text-xs flex items-center gap-3 shadow-sm">
                      <CircularProgress size={20} className="text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-sm text-blue-950">Catalog Update in Progress...</p>
                        <p className="text-blue-800 text-[11px] mt-0.5">
                          Your products are currently being indexed into Handpik's search engine. You can safely close this page or navigate elsewhere.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Failed Sync Banner */}
                  {catalogStatusInfo.status === "failed" && catalogStatusInfo.error && !isStagedMode && (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-950 text-xs flex items-center justify-between gap-3 shadow-sm">
                      <div className="flex items-start gap-2.5">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold text-sm text-red-900">Last Catalog Update Failed</p>
                          <p className="text-red-800 text-[11px] mt-0.5">{catalogStatusInfo.error}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setCatalogStatusInfo((prev) => ({ ...prev, error: null }))}
                        className="text-red-700 hover:text-red-900 text-xs font-bold px-2.5 py-1 rounded-lg bg-red-100 hover:bg-red-200 transition-colors flex-shrink-0"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* Upload Success Banner (Auto-dismisses in 30s or when Update Catalog is clicked) */}
                  {uploadSuccess && catalogStatusInfo.status !== "processing" && (
                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs flex items-center justify-between gap-3 shadow-sm">
                      <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <div>
                          <span className="font-bold text-xs block text-emerald-950">CSV Parsed Successfully - Click Update Catalog to make it live</span>
                          <span className="text-[11px] text-emerald-800">{uploadSuccess}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setUploadSuccess(null)}
                        className="text-emerald-700 hover:text-emerald-900 text-xs font-bold px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* Catalog Sync Complete Banner (Persistent, only dismissed on click) */}
                  {catalogSyncSuccess && (
                    <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-950 text-xs flex flex-wrap items-center justify-between gap-3 shadow-sm">
                      <div className="flex items-center gap-2.5">
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                        <div>
                          <span className="font-bold text-sm block text-emerald-950">Catalog Updated in Qdrant</span>
                          <div className="text-[11px] text-emerald-800 mt-0.5 flex flex-wrap items-center gap-2">
                            <span>{catalogSyncSuccess}</span>
                            {storedBrand?.name && (
                              <a
                                href={`https://handpik.ai/view-all/${encodeURIComponent(storedBrand.name)}`}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => clearBrandSessionCache(storedBrand.name)}
                                className="font-bold text-emerald-700 hover:text-emerald-900 underline inline-flex items-center gap-1 transition-colors"
                              >
                                View your catalog on Handpik <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setCatalogSyncSuccess(null)}
                        className="text-emerald-700 hover:text-emerald-900 text-xs font-bold px-2.5 py-1 rounded-lg bg-emerald-100 hover:bg-emerald-200 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}

                  {/* Upload Error Banner */}
                  {uploadErrors.length > 0 && (
                    <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-900 text-xs space-y-2 shadow-sm">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                          <span className="font-bold text-sm">
                            CSV Validation Failed ({uploadErrors.length} {uploadErrors.length === 1 ? "issue" : "issues"})
                          </span>
                        </div>
                        <button
                          onClick={() => setUploadErrors([])}
                          className="text-red-700 hover:text-red-900 text-xs font-bold"
                        >
                          Dismiss
                        </button>
                      </div>
                      <p className="text-[11px] text-red-700">
                        Please correct the issues in your spreadsheet and upload again:
                      </p>
                      <div className="max-h-48 overflow-y-auto bg-white/70 p-3 rounded-lg border border-red-200 space-y-1 font-mono text-[11px] text-red-800">
                        {uploadErrors.map((err, i) => (
                          <div key={i} className="flex items-start gap-1.5">
                            <span className="text-red-500 select-none">•</span>
                            <span>{err}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Client Pre-validation Errors */}
                  {clientErrors.length > 0 && (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-center gap-2.5">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <div>
                        {clientErrors.map((err, i) => (
                          <div key={i} className="font-semibold">{err}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* CSV Uploader Dropzone (Hidden when catalog is staged or live, unless user toggles it) */}
                  {((!catalogData?.hasCatalog && !isStagedMode) || showUploadDropzone) && (
                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 space-y-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 className="font-bold text-gray-900 text-base mb-1">
                            {catalogData?.hasCatalog ? "Upload New / Updated Catalog" : "Upload Your Product Catalog"}
                          </h3>
                          <p className="text-xs text-gray-500">
                            Upload your catalog spreadsheet in <code>.csv</code> format. Existing products with unchanged SKUs will automatically preserve their identity and tracking stats.
                          </p>
                        </div>
                        {(catalogData?.hasCatalog || isStagedMode) && (
                          <button
                            type="button"
                            onClick={() => setShowUploadDropzone(false)}
                            className="text-xs font-semibold text-gray-600 hover:text-gray-900 px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                          >
                            Hide Uploader
                          </button>
                        )}
                      </div>

                      <form onSubmit={handleUploadCsv} className="space-y-4">
                        <div
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (e.dataTransfer.files && e.dataTransfer.files[0]) {
                              handleFileChange(e.dataTransfer.files[0]);
                            }
                          }}
                          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${selectedFile
                            ? "border-emerald-400 bg-emerald-50/20"
                            : "border-gray-300 hover:border-indigo-400 hover:bg-indigo-50/10"
                            }`}
                          onClick={() => document.getElementById("csvFileInput")?.click()}
                        >
                          <input
                            id="csvFileInput"
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleFileChange(e.target.files[0]);
                              }
                            }}
                          />
                          <div className="flex flex-col items-center justify-center gap-2">
                            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-full">
                              <Upload className="w-6 h-6" />
                            </div>
                            <div className="text-sm font-semibold text-gray-800">
                              {selectedFile ? selectedFile.name : "Click to browse or drag and drop your .csv file here"}
                            </div>
                            <p className="text-xs text-gray-400">
                              Supports (.csv) up to 100MB
                            </p>
                          </div>
                        </div>

                        {filePreview && (
                          <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl flex flex-wrap items-center justify-between gap-3 text-xs">
                            <div>
                              <div className="font-bold text-gray-900">{filePreview.fileName}</div>
                              <div className="text-gray-500 text-[11px] mt-0.5">
                                Size: {filePreview.fileSize} | Estimated products: {filePreview.rowCount} rows
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {showUploadDropzone && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedFile(null);
                                    setFilePreview(null);
                                    setShowUploadDropzone(false);
                                  }}
                                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-100 font-medium text-xs"
                                >
                                  Cancel
                                </button>
                              )}

                              <button
                                type="submit"
                                disabled={uploading || clientErrors.length > 0}
                                className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5 text-xs"
                              >
                                {uploading ? (
                                  <>
                                    <CircularProgress size={14} color="inherit" />
                                    Validating & Processing...
                                  </>
                                ) : (
                                  <>
                                    <Upload className="w-3.5 h-3.5" />
                                    Upload Selected CSV
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                      </form>

                      {/* Need Help Link */}
                      <div className="pt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setCatalogSubView("guide")}
                          className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline flex items-center gap-1"
                        >
                          Need help? View sample template and instructions &rarr;
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Staged Preview Mode Banner with Actions */}
                  {isStagedMode && stagedProducts.length > 0 && (
                    <div className="p-4 rounded-xl bg-amber-50 border border-amber-200 text-amber-950 text-xs flex flex-wrap items-center justify-between gap-3 shadow-sm">
                      <div className="flex items-center gap-2.5">
                        <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <div>
                          <span className="font-bold text-sm block">
                            Staged Catalog Preview ({stagedProducts.length} {stagedProducts.length === 1 ? "product" : "products"} parsed)
                          </span>
                          <span className="text-[11px] text-amber-800">
                            Review the products below. Click "Update Catalog" to make them live on Handpik.
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShowUploadDropzone(!showUploadDropzone);
                            setSelectedFile(null);
                            setFilePreview(null);
                          }}
                          className="px-3.5 py-1.5 bg-white text-gray-700 hover:bg-gray-100 rounded-lg text-xs font-semibold border border-gray-300 transition-colors shadow-sm flex items-center gap-1.5"
                        >
                          <Upload className="w-3.5 h-3.5 text-gray-500" />
                          {showUploadDropzone ? "Hide Uploader" : "Upload Different CSV"}
                        </button>
                        <button
                          onClick={handleDiscardStaged}
                          className="px-3.5 py-1.5 bg-white text-gray-700 hover:bg-gray-100 rounded-lg text-xs font-semibold border border-gray-300 transition-colors shadow-sm"
                        >
                          Discard Changes
                        </button>
                        <button
                          onClick={handleInitiateSync}
                          disabled={syncing}
                          className="px-4 py-1.5 bg-emerald-600 text-white hover:bg-emerald-700 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-sm disabled:opacity-50"
                        >
                          {syncing ? <CircularProgress size={12} color="inherit" /> : <Sparkles className="w-3.5 h-3.5" />}
                          Update Catalog
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Products Table (Renders either Staged Preview or Live Catalog) */}
                  {(isStagedMode ? stagedProducts.length > 0 : catalogData?.hasCatalog) && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                      <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3 bg-gray-50">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-bold text-gray-900 text-sm">
                              {isStagedMode
                                ? `Staged Preview (${stagedProducts.length} items)`
                                : `Active Products Catalog (${catalogData.count} items)`}
                            </h3>
                            {isStagedMode && (
                              <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                                Not Synced Yet
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {isStagedMode
                              ? "Uploaded spreadsheet preview awaiting sync"
                              : catalogStatusInfo.updatedAt
                                ? `Catalog Updated on ${new Date(catalogStatusInfo.updatedAt).toLocaleString()}`
                                : catalogData?.updatedAt
                                  ? `Last updated: ${new Date(catalogData.updatedAt).toLocaleString()}`
                                  : "Live on search engine"}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className="relative">
                            <input
                              type="text"
                              placeholder="Filter by title or SKU..."
                              value={catalogSearch}
                              onChange={(e) => setCatalogSearch(e.target.value)}
                              className="border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48 sm:w-60"
                            />
                            <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-2.5" />
                          </div>
                          <button
                            onClick={() => {
                              if (isStagedMode) {
                                fetchStagedPreview();
                              } else {
                                fetchCatalog();
                              }
                            }}
                            className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border"
                            title="Refresh List"
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {catalogLoading ? (
                        <div className="p-12 flex justify-center text-indigo-600 animate-pulse">
                          <CircularProgress size={28} />
                        </div>
                      ) : (
                        <>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 text-left text-xs">
                              <thead className="bg-gray-50 text-gray-600 font-semibold uppercase">
                                <tr>
                                  <th className="px-4 py-3 w-80 max-w-xs">Product</th>
                                  <th className="px-4 py-3 w-28 whitespace-nowrap">SKU</th>
                                  <th className="px-4 py-3 w-32 text-right whitespace-nowrap">Price</th>
                                  <th className="px-4 py-3 w-48">Categories</th>
                                  <th className="px-4 py-3 w-44">Variants</th>
                                  {!isStagedMode && (
                                    <th className="px-4 py-3 text-right w-28 whitespace-nowrap">Action</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 text-gray-700">
                                {(() => {
                                  const sourceList = isStagedMode ? stagedProducts : (catalogData?.products || []);
                                  const filtered = sourceList.filter((p) => {
                                    if (!catalogSearch) return true;
                                    const q = catalogSearch.toLowerCase();
                                    return (
                                      p.title?.toLowerCase().includes(q) ||
                                      p.sku?.toLowerCase().includes(q)
                                    );
                                  });

                                  if (filtered.length === 0) {
                                    return (
                                      <tr>
                                        <td colSpan={isStagedMode ? 5 : 6} className="px-4 py-8 text-center text-gray-400">
                                          No products matching "{catalogSearch}".
                                        </td>
                                      </tr>
                                    );
                                  }

                                  return filtered.map((prod, idx) => {
                                    const brandSlug = (storedBrand?.name || "").toLowerCase().replace(/[^a-z0-9]/g, "");
                                    const skuSlug = String(prod.sku || "").toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-");
                                    const itemUrl = prod.product_url || (brandSlug && skuSlug ? `https://handpik.ai/onboardedbrands/${brandSlug}/${skuSlug}` : null);

                                    return (
                                      <tr key={prod.sku || idx} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 w-80 max-w-xs">
                                          <div className="flex items-start gap-3">
                                            {prod.images?.[0] || prod.image ? (
                                              <img
                                                src={prod.images?.[0] || prod.image}
                                                alt=""
                                                referrerPolicy="no-referrer"
                                                className="w-12 h-12 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                                                onError={(e) => {
                                                  e.target.style.display = "none";
                                                }}
                                              />
                                            ) : (
                                              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 flex-shrink-0">
                                                <Package className="w-5 h-5" />
                                              </div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                              <span className="font-semibold text-gray-900 block truncate" title={prod.title}>
                                                {prod.title}
                                              </span>
                                              <p className="text-[11px] text-gray-500 line-clamp-2 mt-0.5 break-words leading-relaxed" title={prod.description}>
                                                {prod.description}
                                              </p>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3">
                                          <span className="font-mono bg-gray-100 text-gray-800 px-2 py-0.5 rounded font-semibold text-[11px]">
                                            {prod.sku}
                                          </span>
                                        </td>
                                        <td className="px-4 py-3 text-right whitespace-nowrap">
                                          {prod.sale_price ? (
                                            <div>
                                              <span className="font-bold text-emerald-600 block">
                                                PKR {parseFloat(prod.sale_price).toLocaleString()}
                                              </span>
                                              <span className="text-[10px] text-gray-400 line-through">
                                                PKR {parseFloat(prod.original_price).toLocaleString()}
                                              </span>
                                            </div>
                                          ) : (
                                            <span className="font-medium text-gray-900">
                                              PKR {parseFloat(prod.original_price).toLocaleString()}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                                            {Array.isArray(prod.category) &&
                                              prod.category.map((cat, cIdx) => (
                                                <span
                                                  key={cIdx}
                                                  className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 text-[10px] font-medium"
                                                >
                                                  {cat}
                                                </span>
                                              ))}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3">
                                          <div className="flex flex-wrap gap-1 max-w-[160px]">
                                            {Array.isArray(prod.variants) &&
                                              prod.variants.map((v, vIdx) => (
                                                <span
                                                  key={vIdx}
                                                  className="px-1.5 py-0.5 rounded border border-gray-200 text-gray-600 text-[10px]"
                                                >
                                                  {v.size}
                                                  {v.color ? ` (${v.color})` : ""}
                                                </span>
                                              ))}
                                          </div>
                                        </td>
                                        {!isStagedMode && (
                                          <td className="px-4 py-3 text-right whitespace-nowrap">
                                            {itemUrl ? (
                                              <a
                                                href={itemUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-900 border border-indigo-200 text-xs font-semibold transition-colors shadow-sm"
                                              >
                                                View Product <ExternalLink className="w-3.5 h-3.5" />
                                              </a>
                                            ) : (
                                              <span className="text-gray-400">-</span>
                                            )}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                            </table>
                          </div>

                          {!isStagedMode && catalogData?.pagination && catalogData.pagination.totalPages > 1 && (
                            <Pagination pagination={catalogData.pagination} onPageChange={setCatalogPage} />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* 7. BRAND SETTINGS TAB */}
          {activeTab === "settings" && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8 max-w-2xl">
              <h2 className="text-lg font-bold text-gray-900 mb-1">Brand Account Settings</h2>
              <p className="text-xs text-gray-500 mb-5">Manage your brand contact details for customer inquiries</p>

              <form onSubmit={handleSaveSettings} className="space-y-4 text-xs">
                <div>
                  <label className="block text-gray-500 font-medium uppercase mb-1">Brand Name</label>
                  <div className="font-semibold text-gray-900 text-sm bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {storedBrand?.name}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-500 font-medium uppercase mb-1">Login Email</label>
                  <div className="font-medium text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-200">
                    {storedBrand?.email}
                  </div>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">WhatsApp Contact</label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex items-center w-24 flex-shrink-0">
                      <span className="absolute left-2.5 text-xs font-semibold text-gray-500 select-none">+</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        placeholder="92"
                        maxLength={5}
                        value={settingsCountryCode}
                        onChange={(e) => setSettingsCountryCode(e.target.value.replace(/[^0-9]/g, ""))}
                        className="w-full border border-gray-300 rounded-lg py-2.5 pl-6 pr-2 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-center font-mono"
                      />
                    </div>
                    <input
                      type="tel"
                      inputMode="numeric"
                      placeholder="1234567890"
                      value={settingsPhoneNumber}
                      onChange={(e) => setSettingsPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))}
                      className="flex-1 border border-gray-300 rounded-lg p-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">Customers will use this number for direct WhatsApp product inquiries</p>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-1">Instagram Handle</label>
                  <div className="flex items-center">
                    <span className="inline-flex items-center px-3 py-2.5 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-xs font-semibold">
                      @
                    </span>
                    <input
                      type="text"
                      placeholder="brand_pk"
                      value={settingsInstagram}
                      onChange={(e) => setSettingsInstagram(e.target.value)}
                      className="w-full border border-gray-300 rounded-r-lg p-2.5 text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">Your official Instagram profile handle</p>
                </div>

                {settingsSuccess && (
                  <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 font-medium text-xs flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    Brand settings updated successfully!
                  </div>
                )}

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={settingsSaving}
                    className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors shadow-sm disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {settingsSaving ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </main>

        {/* Direct Order Details Popup Modal */}
        {selectedDirectOrder && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs animate-fadeIn"
            onClick={() => setSelectedDirectOrder(null)}
          >
            <div
              className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-lg w-full max-h-[90vh] overflow-y-auto flex flex-col animate-scaleUp"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="p-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-50/50 to-white">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-700 shadow-xs">
                    <ShoppingBag className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">Direct Order Details</h3>
                    <p className="text-xs text-gray-500">
                      Placed on {formatTimestamp(selectedDirectOrder.click_date)}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedDirectOrder(null)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-5 space-y-4">
                {/* Customer Information Card */}
                <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-200/80 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200/60 text-xs font-bold text-gray-800 uppercase tracking-wider">
                    <User className="w-4 h-4 text-indigo-600" />
                    <span>Customer Information</span>
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-gray-500 font-medium">Customer Name:</span>
                      <span className="font-bold text-gray-900 text-right text-sm">
                        {selectedDirectOrder.customer_name || "N/A"}
                      </span>
                    </div>

                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
                      <span className="text-gray-500 font-medium">Phone / WhatsApp:</span>
                      <div className="flex items-center gap-2 flex-wrap">
                        {selectedDirectOrder.customer_phone && (
                          <button
                            onClick={() => handleCopyPhone(selectedDirectOrder.customer_phone)}
                            className="p-1.5 text-gray-500 hover:text-indigo-600 rounded-md hover:bg-gray-100 border border-gray-200 transition-colors cursor-pointer"
                            title="Copy Phone Number"
                          >
                            {copiedPhone ? (
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            ) : (
                              <Copy className="w-3.5 h-3.5" />
                            )}
                          </button>
                        )}
                        <span className="font-bold font-mono text-gray-900 text-sm">
                          {selectedDirectOrder.customer_phone || "N/A"}
                        </span>
                        {(() => {
                          const cleanPhone = (selectedDirectOrder.customer_phone || "").replace(/[^0-9]/g, "");
                          if (!cleanPhone) return null;
                          return (
                            <a
                              href={`https://wa.me/${cleanPhone}`}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200 text-xs font-semibold transition-colors shadow-xs"
                              title="Chat with Customer on WhatsApp"
                            >
                              <WhatsAppIcon className="w-3.5 h-3.5" fill="#25D366" />
                              <span>Chat on Whatsapp</span>
                            </a>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="flex justify-between items-start gap-2 pt-2 border-t border-gray-200/40">
                      <span className="text-gray-500 font-medium flex items-center gap-1 flex-shrink-0">
                        <MapPin className="w-4 h-4 text-gray-400" /> Delivery Address:
                      </span>
                      <span className="font-medium text-gray-900 text-right break-words max-w-[280px] text-sm">
                        {selectedDirectOrder.customer_address || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Product Information Card */}
                <div className="bg-gray-50/80 rounded-xl p-4 border border-gray-200/80 space-y-3">
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200/60 text-xs font-bold text-gray-800 uppercase tracking-wider">
                    <Package className="w-4 h-4 text-indigo-600" />
                    <span>Product Ordered</span>
                  </div>

                  <div className="flex items-start gap-3.5">
                    <div className="w-16 h-17 bg-white rounded-lg border border-gray-200 overflow-hidden flex-shrink-0 shadow-xs">
                      {selectedDirectOrder.product_image ? (
                        <img
                          src={selectedDirectOrder.product_image}
                          alt={selectedDirectOrder.product_title || "Product"}
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Package className="w-8 h-8 m-auto text-gray-400" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0 space-y-1.5">
                      {/* SKU on top */}
                      <div className="font-mono text-sm font-bold text-gray-800">
                        SKU: {selectedDirectOrder.product_id || "N/A"}
                      </div>
                      {/* Title */}
                      <h4 className="font-bold text-gray-900 text-sm leading-snug line-clamp-2" title={selectedDirectOrder.product_title}>
                        {selectedDirectOrder.product_title || "Product"}
                      </h4>
                      {/* Price */}
                      <div className="font-bold text-emerald-600 text-sm">
                        {selectedDirectOrder.currency || "PKR"}{" "}
                        {parseFloat(selectedDirectOrder.product_price || 0).toLocaleString()}
                      </div>

                      {/* Size & Color */}
                      {(selectedDirectOrder.selected_size || selectedDirectOrder.selected_color) && (
                        <div className="flex flex-wrap gap-2 pt-0.5">
                          {selectedDirectOrder.selected_size && (
                            <span className="px-2.5 py-1 rounded-md bg-white text-gray-700 text-sm font-medium border border-gray-200 shadow-2xs">
                              Size: <span className="font-semibold text-gray-900">{selectedDirectOrder.selected_size}</span>
                            </span>
                          )}
                          {selectedDirectOrder.selected_color && (
                            <span className="px-2.5 py-1 rounded-md bg-white text-gray-700 text-sm font-medium border border-gray-200 shadow-2xs">
                              Color: <span className="font-semibold text-gray-900">{selectedDirectOrder.selected_color}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {(() => {
                    const modalUrl =
                      selectedDirectOrder.product_url ||
                      (storedBrand?.name && selectedDirectOrder.product_id
                        ? `https://handpik.ai/onboardedbrands/${storedBrand.name.toLowerCase().replace(/[^a-z0-9]/g, "")}/${String(selectedDirectOrder.product_id).toLowerCase().replace(/[^a-z0-9-_]/g, "-").replace(/-+/g, "-")}`
                        : null);
                    if (!modalUrl) return null;
                    return (
                      <div className="pt-2">
                        <a
                          href={modalUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 hover:text-indigo-900 border border-indigo-200 text-xs font-semibold transition-colors shadow-xs"
                        >
                          View Product on Handpik <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-100 flex justify-end bg-gray-50 rounded-b-2xl">
                <button
                  onClick={() => setSelectedDirectOrder(null)}
                  className="px-4 py-2 bg-white text-gray-700 hover:bg-gray-100 border border-gray-200 rounded-xl text-xs font-semibold transition-colors shadow-xs"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* In-App Confirmation & Alert Modal (Replaces browser window.confirm & alert) */}
        {modalConfig && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-gray-200 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`p-2.5 rounded-xl ${modalConfig.type === "danger"
                      ? "bg-red-50 text-red-600"
                      : modalConfig.type === "success"
                        ? "bg-emerald-50 text-emerald-600"
                        : "bg-indigo-50 text-indigo-600"
                      }`}
                  >
                    {modalConfig.type === "danger" ? (
                      <AlertCircle className="w-5 h-5" />
                    ) : modalConfig.type === "success" ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Sparkles className="w-5 h-5" />
                    )}
                  </div>
                  <h3 className="text-base font-bold text-gray-900">{modalConfig.title}</h3>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed pl-11">
                  {modalConfig.message}
                </p>
              </div>
              <div className="bg-gray-50 px-6 py-3.5 border-t border-gray-100 flex items-center justify-end gap-2.5">
                {modalConfig.onCancel && (
                  <button
                    type="button"
                    onClick={() => setModalConfig(null)}
                    className="px-4 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    {modalConfig.cancelText || "Cancel"}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    const action = modalConfig.onConfirm;
                    setModalConfig(null);
                    if (action) action();
                  }}
                  className={`px-4 py-2 text-xs font-semibold text-white rounded-lg transition-colors shadow-sm ${modalConfig.type === "danger"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-indigo-600 hover:bg-indigo-700"
                    }`}
                >
                  {modalConfig.confirmText || "OK"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
